class ProductAddons extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.form = document.querySelector('form[action*="/cart/add"]');

    if (this.form) {
      this.variantSelectors = this.form.closest('section').querySelectorAll('variant-selectors, variant-radios');
      
      this.variantSelectors.forEach(selector => {
        selector.addEventListener('change', () => {
          this.showLoadingState();
          setTimeout(() => this.onVariantChange(), 100);
        });
      });

      const productSection = this.form.closest('section');
      if (productSection) {
        productSection.addEventListener('variant:change', (event) => {
          this.showLoadingState();
          setTimeout(() => this.onVariantChange(), 100);
        });
      }

      // Initial load
      this.onVariantChange();
    }
  }

  showLoadingState() {
    const addonsContainer = document.querySelector('.product-addons');
    if (!addonsContainer) return;
    
    const loadingSpinner = document.createElement('div');
    loadingSpinner.className = 'product-addons__loading';
    loadingSpinner.innerHTML = '<div class="variant-loading-spinner"></div>';
    
    // Clear existing content and show spinner
    addonsContainer.innerHTML = '';
    addonsContainer.style.display = 'block';
    addonsContainer.appendChild(loadingSpinner);
  }

  onVariantChange() {
    const variantIdInput = this.form.querySelector('input[name="id"], select[name="id"]');
    const variantId = variantIdInput?.value;
    
    if (!variantId) return;

    const variantData = this.getVariantData();
    const currentVariant = variantData.find(v => v.id == variantId);
    
    this.updateAddons(currentVariant);
  }

  async initializeCartDrawer() {
    let cartDrawer = document.querySelector('cart-drawer');
    
    if (!cartDrawer) {
      // If cart drawer doesn't exist, fetch and insert it
      try {
        const response = await fetch('?section_id=cart-drawer');
        const html = await response.text();
        
        // Create temporary container and insert the HTML
        const container = document.createElement('div');
        container.innerHTML = html;
        
        // Get the cart drawer element
        const newDrawer = container.querySelector('cart-drawer');
        
        if (newDrawer) {
          // Insert the drawer into the DOM
          document.body.appendChild(newDrawer);
          cartDrawer = newDrawer;
          
          // Initialize the drawer if needed
          if (typeof cartDrawer.open === 'function') {
            await new Promise(resolve => setTimeout(resolve, 100)); // Give time for initialization
          }
        }
      } catch (error) {
        console.error('Error initializing cart drawer:', error);
        return null;
      }
    }
    
    return cartDrawer;
  }

  async updateCartDrawer() {
    try {
      const cartDrawer = await this.initializeCartDrawer();
      if (!cartDrawer) return;

      // Fetch fresh cart drawer content
      const response = await fetch('?section_id=cart-drawer');
      const html = await response.text();
      
      const container = document.createElement('div');
      container.innerHTML = html;
      
      const newDrawerContent = container.querySelector('cart-drawer');
      if (newDrawerContent) {
        // Update inner content while preserving the drawer element
        cartDrawer.innerHTML = newDrawerContent.innerHTML;
      }

      // Open the drawer
      if (typeof cartDrawer.show === 'function') {
        cartDrawer.show();
      } else if (cartDrawer.hasAttribute('open') === false) {
        cartDrawer.setAttribute('open', '');
      }

    } catch (error) {
      console.error('Error updating cart drawer:', error);
    }
  }

  async updateCart(cartData = null) {
    try {
      if (!cartData) {
        // Fetch current cart state if not provided
        const response = await fetch(`${window.Shopify.routes.root}cart.js`);
        cartData = await response.json();
      }

      // Update cart count bubble
      const bubbles = document.querySelectorAll('.cart-count-bubble span');
      bubbles.forEach(bubble => {
        if (bubble) {
          bubble.textContent = cartData.item_count;
          bubble.closest('.cart-count-bubble').style.display = cartData.item_count > 0 ? 'flex' : 'none';
        }
      });

      // Update cart drawer
      await this.updateCartDrawer();

      // Fallback for cart notification if drawer update fails
      const cartNotification = document.querySelector('cart-notification');
      if (cartNotification && !document.querySelector('cart-drawer')) {
        cartNotification.renderContents(cartData);
      }
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  }

  updateAddons(variantData) {
    const addonsContainer = document.querySelector('.product-addons');
    if (!addonsContainer) return;

    if (variantData?.addon_products?.length > 0) {
      const addonContent = `
        <h2 class="product-addons__title">Add More Options:</h2>
        <div class="product-addons__list">
          ${this.generateAddonsHTML(variantData.addon_products)}
        </div>
      `;
      addonsContainer.innerHTML = addonContent;
      addonsContainer.style.display = 'block';
      this.setupAddToCartListeners();
    } else {
      addonsContainer.style.display = 'none';
    }
  }

  getVariantData() {
    if (!this.variantData) {
      const jsonScript = this.querySelector('[type="application/json"]');
      if (jsonScript) {
        try {
          this.variantData = JSON.parse(jsonScript.textContent);
        } catch (e) {
          console.error('Error parsing JSON:', e);
          this.variantData = [];
        }
      } else {
        console.error('No variant data script found');
        this.variantData = [];
      }
    }
    return this.variantData;
  }

  formatMoney(cents) {
    if (typeof cents !== 'number') {
      cents = parseFloat(cents);
    }
    
    if (isNaN(cents) || cents === null) {
      return '$0.00';
    }

    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  generateAddonsHTML(addons) {
    return addons.map(addon => {
      let imageUrl = '';
      if (addon.featured_image?.url) {
        imageUrl = addon.featured_image.url;
      } else if (typeof addon.featured_image === 'string') {
        imageUrl = addon.featured_image;
      }
      
      if (imageUrl && imageUrl.includes('cdn.shopify.com') && !imageUrl.includes('_150x')) {
        imageUrl = imageUrl.replace(/\.(jpg|jpeg|png|gif|webp)/i, '_150x150.$1');
      }

      return `
        <div class="product-addon-item">
          <div class="product-addon-item__image">
            <img src="${imageUrl}" 
                 alt="${addon.title}"
                 width="150"
                 height="150"
                 loading="lazy">
          </div>
          <div class="product-addon-item__content">
            <h3 class="product-addon-item__title">${addon.title}</h3>
            <div class="product-addon-item__price">
              <span class="price">${this.formatMoney(addon.price)}</span>
              ${addon.compare_at_price > addon.price 
                ? `<span class="save-text">Save: ${this.formatMoney(addon.compare_at_price - addon.price)}</span>` 
                : ''}
            </div>
          </div>
          <button type="button" 
                  class="product-addon-item__add-button"
                  data-addon-variant-id="${addon.variants?.[0]?.id}"
                  data-addon-product-handle="${addon.handle}">
            + Add
          </button>
        </div>
      `;
    }).join('');
  }

  setupAddToCartListeners() {
    const addButtons = document.querySelectorAll('.product-addon-item__add-button');
    addButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        await this.handleAddonAdd(e);
      });
    });
  }









  async handleAddonAdd(event) {
    const button = event.currentTarget;
    const variantId = button.dataset.addonVariantId;
    
    if (!variantId) {
      console.error('No variant ID for addon');
      return;
    }
    
    button.disabled = true;
    const originalText = button.textContent;
    button.innerHTML = '<span class="loading-spinner"></span>';
    
    try {
      // 1. Add to cart
      const formData = {
        items: [{
          id: variantId,
          quantity: 1
        }]
      };

      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Add to cart failed');
      const cartData = await response.json();

      // 2. Get fresh cart drawer markup
      const cartDrawerResponse = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
      const cartDrawerHtml = await cartDrawerResponse.text();
      
      // 3. Handle cart drawer update
      await this.updateCartDrawerSafely(cartDrawerHtml, cartData);

      button.textContent = '✓ Added';

    } catch (error) {
      console.error('Error adding to cart:', error);
      button.textContent = 'Error';
    } finally {
      setTimeout(() => {
        button.disabled = false;
        button.textContent = originalText;
      }, 2000);
    }
  }

  async updateCartDrawerSafely(cartDrawerHtml, cartData) {
    try {
      let cartDrawer = document.querySelector('cart-drawer');
      const parser = new DOMParser();
      const doc = parser.parseFromString(cartDrawerHtml, 'text/html');
      const newDrawerContent = doc.querySelector('cart-drawer');

      // If cart drawer doesn't exist, create it
      if (!cartDrawer) {
        cartDrawer = document.createElement('cart-drawer');
        document.body.appendChild(cartDrawer);
      }

      if (newDrawerContent) {
        // Safely update the drawer content
        cartDrawer.innerHTML = newDrawerContent.innerHTML;

        // Ensure cart-drawer element has required attributes
        cartDrawer.setAttribute('class', newDrawerContent.getAttribute('class') || '');
        
        // Update cart count bubble
        this.updateCartCount(cartData.item_count);

        // Wait for next frame to ensure DOM is updated
        await new Promise(requestAnimationFrame);

        // Initialize any quantity inputs
        const quantityInputs = cartDrawer.querySelectorAll('quantity-input');
        quantityInputs.forEach(input => {
          if (typeof input.init === 'function') {
            input.init();
          }
        });

        // Dispatch drawer open event after a short delay
        setTimeout(() => {
          const drawerOpenEvent = new CustomEvent('drawer:open', {
            bubbles: true,
            detail: { drawer: cartDrawer }
          });
          document.dispatchEvent(drawerOpenEvent);
        }, 100);
      }
    } catch (error) {
      console.error('Error updating cart drawer:', error);
    }
  }

  updateCartCount(itemCount) {
    const bubbles = document.querySelectorAll('.cart-count-bubble span');
    bubbles.forEach(bubble => {
      if (bubble) {
        bubble.textContent = itemCount;
        const bubbleContainer = bubble.closest('.cart-count-bubble');
        if (bubbleContainer) {
          bubbleContainer.style.display = itemCount > 0 ? 'flex' : 'none';
          
          // Force a reflow to ensure the display change takes effect
          void bubbleContainer.offsetHeight;
        }
      }
    });
  }









}

// Add loading spinner styles
const style = document.createElement('style');
style.textContent = `
  .loading-spinner {
    display: inline-block;
    width: 20px;
    height: 20px;
    border: 2px solid rgba(255,255,255,0.3);
    border-radius: 50%;
    border-top-color: #fff;
    animation: spin 0.8s linear infinite;
  }
  
  .variant-loading-spinner {
    display: inline-block;
    width: 40px;
    height: 40px;
    border: 3px solid rgba(0,0,0,0.1);
    border-radius: 50%;
    border-top-color: #000;
    animation: spin 1s linear infinite;
  }
  
  .product-addons__loading {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 150px;
    width: 100%;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);

customElements.define('product-addons', ProductAddons);